/**
 * Fecha, hora y clave de idempotencia para los formularios de marca manual y corrección
 * (T050 · US3, reutilizado por T058 · US4).
 *
 * `CreateManualMarkBodySchema`/`CreateCorrectionBodySchema` piden `date`/`time` en la zona
 * **vigente** de la sede (`LocalDateSchema`/`LocalTimeSchema`, wiring §0.5): la conversión a
 * instante UTC la hace el servidor con `localDateTimeToUtc`. Aquí solo se calculan los valores
 * por defecto (ahora, en esa zona) para no forzar a escribir la fecha de hoy a mano.
 */

/** `YYYY-MM-DD` de `date` en `timeZone` (formato de `LocalDateSchema`). */
export function localDateInZone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** `HH:mm` (24 h) de `date` en `timeZone` (formato de `LocalTimeSchema`). */
export function localTimeInZone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/**
 * Clave de idempotencia nueva (`IdempotencyKeySchema` = `z.uuid()`). Una por **apertura** del
 * diálogo (FR-007/FR-009): reenviar tras `CONFIRMATION_REQUIRED` reusa la misma, nunca genera una
 * nueva, para no crear dos marcas si el admin reintenta. Mismo patrón de
 * `apps/staff/src/features/attendance/use-marking.ts#defaultIdempotencyKey`.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Sin `randomUUID` (navegador antiguo): UUIDv4 con `getRandomValues`.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
